/**
 * SBOT · brinde automático da gaveta (bloco `gift` do tema-ref) no Horizon.
 *
 * O tema-ref põe/tira o produto-brinde do carrinho SOZINHO (assets/main.js do tema-ref: CartDrawer.renderContents e
 * CartItems.updateQuantity varrem os <cart-drawer-gift> depois de cada render; CartDrawerUpsell.addToCart/removeFromCart):
 * compara o cartão destravado pela meta (Liquid) com a presença do produto no carrinho e faz UMA ação por render,
 * remoção antes de inclusão. Aqui o mesmo, sem os furos medidos no tema-ref (16/10, retro-pia-v6: o add pela PDP não passava
 * pela lógica e o brinde só entrava na página seguinte — e entrou DUPLICADO):
 *   - o estado vem do servidor (snippets/sbot-cart-gift.liquid): data-unlocked, data-in-cart, data-has-gift-line e
 *     data-sbot-stamp (assinatura do carrinho daquela renderização);
 *   - gatilho = toda re-renderização da gaveta (observador na seção cart-drawer-section: vale para o add do tema, do molde
 *     KB, de apps via SbotCart.refresh) + o carregamento da página. Na página /cart, onde a gaveta do Horizon não existe,
 *     a seção traz só o estado e é re-renderizada aqui depois de cada mudança do carrinho;
 *   - antes de mexer, lê o /cart.js: a presença é a REAL (nunca duplica) e renderização velha não manda em nada;
 *   - inclui com POST /cart/add.js pedindo as seções do carrinho + CartLinesUpdateEvent: o Horizon re-renderiza a
 *     gaveta, a bolha e o anúncio de leitor de tela. Ação 'update', não 'add': o tema-ref não abria a gaveta por causa do
 *     brinde (renderContents(…, isCartUpsell=true)) e o auto-open do Horizon só reage a 'add';
 *   - a linha incluída leva a propriedade privada `_sbot_gift` (o Horizon e o checkout não mostram propriedade com
 *     "_"): a remoção só tira linha de brinde (marcada ou de preço 0) — um item que o cliente comprou nunca some;
 *   - trava anti-laço: fila única (uma ação por vez), no máximo 4 ações por brinde em 30 s e 30 por página.
 */
import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

const SECTION_ID = 'cart-drawer-section';
const GIFT_PROPERTY = '_sbot_gift';
const WINDOW_MS = 30000;
const MAX_PER_GIFT = 4;
const MAX_PER_PAGE = 30;
const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

const state = {
  busy: false,
  dirty: false,
  timer: 0,
  actions: 0,
  batch: true,
  /** @type {Map<string, number[]>} */
  history: new Map(),
  /** @type {Map<string, string>} variante escolhida no seletor do cartão, por produto */
  chosen: new Map(),
  renderedFor: '',
  warned: new Set(),
  /** @type {Set<string>} brindes que a loja recusou incluir nesta página (esgotado) */
  refused: new Set(),
  scheduled: false,
  gating: false,
};

/** `Theme` é um const global de snippets/scripts.liquid (não vira propriedade de window). */
function routes() {
  // eslint-disable-next-line no-undef
  const themeRoutes = typeof Theme !== 'undefined' ? Theme.routes : null;
  return {
    add: themeRoutes?.cart_add_url ?? '/cart/add.js',
    update: `${themeRoutes?.cart_update_url ?? '/cart/update'}.js`,
    cart: `${themeRoutes?.cart_url ?? '/cart'}.js`,
  };
}

/** Na página /cart o Horizon não renderiza a gaveta (snippets/cart-drawer.liquid: template.name != 'cart'). */
function hasDrawer() {
  return Boolean(document.getElementById('cart-drawer'));
}

/** Mesma regra do product-form do Horizon: toda seção de carrinho presente na página vem junto. */
function sectionIds() {
  const ids = new Set([SECTION_ID]);
  for (const element of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  }
  return [...ids];
}

async function fetchCart() {
  const response = await fetch(routes().cart, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`cart.js ${response.status}`);
  return response.json();
}

/** @param {any} cart */
function signature(cart) {
  return `${cart.item_count}-${cart.items_subtotal_price}-${(cart.items || []).length}`;
}

/**
 * Linha de brinde = do produto-brinde E (marcada por nós ou de preço 0). Mesma regra do Liquid.
 * @param {any} item
 * @param {string} productId
 */
function isGiftLine(item, productId) {
  if (String(item.product_id) !== productId) return false;
  return String(item.properties?.[GIFT_PROPERTY] ?? '') === '1' || Number(item.original_price) === 0;
}

/**
 * @typedef {{ element: HTMLElement, productId: string, variantId: string, lineVariant: string, unlocked: boolean,
 *   inCart: boolean, hasGiftLine: boolean, available: boolean, stamp: string }} Gift
 * @returns {Gift[]}
 */
function readGifts() {
  /** @type {Map<string, Gift>} */
  const gifts = new Map();
  for (const element of document.querySelectorAll('[data-sbot-gift]')) {
    if (!(element instanceof HTMLElement)) continue;
    const productId = element.dataset.productId || '';
    if (!productId || gifts.has(productId)) continue;
    const select = element.querySelector('select.sbot-gift__variant');
    gifts.set(productId, {
      element,
      productId,
      variantId:
        state.chosen.get(productId) || (select instanceof HTMLSelectElement ? select.value : '') || element.dataset.variantId || '',
      lineVariant: element.dataset.lineVariant || '',
      unlocked: element.dataset.unlocked === 'true',
      inCart: element.dataset.inCart === 'true',
      hasGiftLine: element.dataset.hasGiftLine === 'true',
      available: element.dataset.available !== 'false',
      stamp: element.dataset.sbotStamp || '',
    });
  }
  return [...gifts.values()];
}

/** Precisa agir, pelo que o servidor renderizou? (filtro barato antes de ler o /cart.js) @param {Gift} gift */
function needsAction(gift) {
  if (!gift.unlocked) return gift.hasGiftLine;
  if (!gift.inCart) return gift.available && Boolean(gift.variantId) && !state.refused.has(gift.productId);
  const chosen = state.chosen.get(gift.productId);
  return Boolean(chosen && gift.lineVariant && chosen !== gift.lineVariant);
}

/** Trava anti-laço (outro app tirando o que pomos, brinde que o servidor recusa…). @param {string} productId */
function mayAct(productId) {
  const now = Date.now();
  const recent = (state.history.get(productId) || []).filter((t) => now - t < WINDOW_MS);
  state.history.set(productId, recent);
  if (state.actions < MAX_PER_PAGE && recent.length < MAX_PER_GIFT) return true;
  if (!state.warned.has(productId)) {
    state.warned.add(productId);
    console.warn(`[sbot-gift] trava anti-laço: brinde ${productId} parou de ser mexido nesta página`);
  }
  return false;
}

/** @param {string[]} productIds */
function recordAction(productIds) {
  const now = Date.now();
  for (const productId of productIds) {
    const recent = state.history.get(productId) || [];
    recent.push(now);
    state.history.set(productId, recent);
  }
  state.actions += 1;
}

/**
 * Anuncia a mudança pelo evento padrão do Horizon (gaveta, bolha, leitor de tela). Na página /cart a nossa seção é
 * morfada aqui (lá ela não é um cart-items-component).
 * @param {'update' | 'remove'} action
 * @param {Array<{ id: string, quantity: number }>} lines
 * @param {any} cart
 * @param {Record<string, string> | undefined} sections
 */
function announce(action, lines, cart, sections) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent(
      /** @type {any} */ ({
        action,
        context: 'cart',
        lines: lines.length ? lines : [{ id: 'sbot-gift', quantity: 0 }],
        promise: deferred.promise,
      })
    )
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { sections, items: cart.items, itemCount: cart.item_count, source: 'sbot-gift', didError: false },
  });
  if (!hasDrawer() && sections?.[SECTION_ID]) {
    morphSection(SECTION_ID, sections[SECTION_ID], { mode: 'full' }).catch(() => {});
  }
}

/** @param {Array<{ productId: string, keys: string[] }>} list */
async function removeLines(list) {
  /** @type {Record<string, number>} */
  const updates = {};
  for (const { keys } of list) for (const key of keys) updates[key] = 0;
  recordAction(list.map((entry) => entry.productId));
  const response = await fetch(routes().update, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ updates, sections: sectionIds().join(','), sections_url: window.location.pathname }),
  });
  const data = await response.json();
  if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || 'cart/update falhou');
  announce(
    'remove',
    Object.keys(updates).map((id) => ({ id, quantity: 0 })),
    data,
    data.sections
  );
}

/** @param {Gift[]} list */
async function addGifts(list) {
  const items = list.map((gift) => ({ id: Number(gift.variantId), quantity: 1, properties: { [GIFT_PROPERTY]: '1' } }));
  recordAction(list.map((gift) => gift.productId));
  const response = await fetch(routes().add, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ items, sections: sectionIds().join(','), sections_url: window.location.pathname }),
  });
  const data = await response.json();
  if (!response.ok || data.status) {
    // brinde recusado pela loja (esgotou, limite de estoque): em lote, passa a incluir um por vez; sozinho, aquele
    // produto sai da fila nesta página. Nada mudou no carrinho (o observador não dispara): a próxima volta é agendada aqui.
    if (list.length > 1) state.batch = false;
    else state.refused.add(list[0].productId);
    state.dirty = true;
    throw new Error(data.description || data.message || 'cart/add falhou');
  }
  const cart = await fetchCart();
  const lines = (Array.isArray(data.items) ? data.items : [data]).map((item) => ({
    id: String(item.key),
    quantity: Number(item.quantity) || 1,
  }));
  announce('update', lines, cart, data.sections);
}

/** Pede ao Horizon a seção do carrinho de novo (o observador chama a avaliação quando ela chegar). */
async function rerender() {
  try {
    await sectionRenderer.renderSection(SECTION_ID, { cache: false, mode: hasDrawer() ? 'hydration' : 'full' });
  } catch (error) {
    if (/** @type {any} */ (error)?.name !== 'AbortError') console.warn('[sbot-gift] re-render falhou:', error);
  }
}

/** Mudança de quantidade do próprio Horizon em curso (4.2.0): espera terminar para não cruzar com ela. */
async function themeSettled() {
  const ctor = /** @type {any} */ (customElements.get('cart-items-component'));
  const pending = ctor?.pendingQuantityUpdates;
  if (Array.isArray(pending) && pending.length) await Promise.allSettled(pending);
}

/** @param {number} [delay] */
function schedule(delay = 150) {
  clearTimeout(state.timer);
  state.scheduled = true;
  state.timer = window.setTimeout(evaluate, delay);
}

/** Ainda vai mexer no carrinho? (avaliação agendada/em curso ou brinde fora do lugar que a trava ainda deixa mexer) */
function pending() {
  if (state.busy || state.scheduled) return true;
  const now = Date.now();
  return readGifts().some((gift) => {
    if (!needsAction(gift)) return false;
    const recent = (state.history.get(gift.productId) || []).filter((t) => now - t < WINDOW_MS);
    return state.actions < MAX_PER_PAGE && recent.length < MAX_PER_GIFT;
  });
}

let checkoutBypass = false;

/**
 * Checkout clicado com o brinde ainda entrando (cliente rápido logo depois de comprar): espera o motor terminar — no
 * máximo 4 s, depois segue de qualquer jeito — e refaz o clique. Roda DEPOIS da trava do termo (sbot-cart-extras.js
 * registra antes, também em captura no window): termo não aceito barra antes de chegar aqui.
 * @param {Event} event
 */
function waitGiftsBeforeCheckout(event) {
  if (checkoutBypass) return;
  /** @type {HTMLElement | null} */
  let submitter = null;
  if (event.type === 'click') {
    const target = event.target instanceof Element ? event.target : null;
    submitter = /** @type {HTMLElement | null} */ (target?.closest('button[name="checkout"], input[name="checkout"]') ?? null);
  } else {
    const sub = /** @type {SubmitEvent} */ (event).submitter;
    if (sub instanceof HTMLElement && sub.getAttribute('name') === 'checkout') submitter = sub;
  }
  if (!submitter || !pending()) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (state.gating) return;
  state.gating = true;
  submitter.setAttribute('aria-busy', 'true');
  const started = Date.now();
  const retry = () => {
    if (pending() && Date.now() - started < 4000) {
      window.setTimeout(retry, 120);
      return;
    }
    state.gating = false;
    submitter.removeAttribute('aria-busy');
    checkoutBypass = true;
    try {
      submitter.click();
    } finally {
      window.setTimeout(() => {
        checkoutBypass = false;
      }, 0);
    }
  };
  window.setTimeout(retry, 120);
}

async function evaluate() {
  state.scheduled = false;
  if (state.busy) {
    state.dirty = true;
    return;
  }
  const gifts = readGifts();
  if (!gifts.some(needsAction)) return;
  state.busy = true;
  try {
    await themeSettled();
    const cart = await fetchCart();
    const sig = signature(cart);
    const stamp = gifts[0]?.stamp;
    if (stamp && stamp !== sig && state.renderedFor !== sig) {
      // a gaveta mostra um carrinho que já mudou: re-renderiza UMA vez para esta assinatura e decide com o estado novo
      state.renderedFor = sig;
      await rerender();
      return;
    }
    /** @type {Array<{ productId: string, keys: string[] }>} */
    const removals = [];
    /** @type {Gift[]} */
    const additions = [];
    for (const gift of gifts) {
      const lines = (cart.items || []).filter((/** @type {any} */ item) => String(item.product_id) === gift.productId);
      const giftKeys = lines.filter((/** @type {any} */ item) => isGiftLine(item, gift.productId)).map((/** @type {any} */ item) => item.key);
      if (!gift.unlocked) {
        if (giftKeys.length && mayAct(gift.productId)) removals.push({ productId: gift.productId, keys: giftKeys });
        continue;
      }
      if (!lines.length) {
        if (gift.available && gift.variantId && !state.refused.has(gift.productId) && mayAct(gift.productId)) {
          additions.push(gift);
        }
        continue;
      }
      // destravado e presente: o cliente trocou a variante no cartão → tira a linha de brinde antiga (a nova entra na próxima volta)
      const chosen = state.chosen.get(gift.productId);
      if (chosen && !lines.some((/** @type {any} */ item) => String(item.variant_id) === chosen)) {
        const stale = lines.filter((/** @type {any} */ item) => isGiftLine(item, gift.productId)).map((/** @type {any} */ item) => item.key);
        if (stale.length && mayAct(gift.productId)) removals.push({ productId: gift.productId, keys: stale });
      }
    }
    if (removals.length) await removeLines(removals);
    else if (additions.length) await addGifts(state.batch ? additions : additions.slice(0, 1));
  } catch (error) {
    console.warn('[sbot-gift] avaliação falhou:', error);
  } finally {
    state.busy = false;
    if (state.dirty) {
      state.dirty = false;
      schedule();
    }
  }
}

/* ------------------------------------------------------------ cartão */

/**
 * Cartão do brinde. Só o seletor de variante precisa de comportamento (o resto é Liquid).
 * @extends {Component}
 */
class SbotGift extends Component {
  /** @param {Event} [event] */
  selectVariant(event) {
    const select =
      event?.target instanceof HTMLSelectElement ? event.target : this.querySelector('select.sbot-gift__variant');
    if (!(select instanceof HTMLSelectElement) || !this.dataset.productId) return;
    state.chosen.set(this.dataset.productId, select.value);
    schedule(0);
  }
}

if (!customElements.get('sbot-gift-component')) customElements.define('sbot-gift-component', SbotGift);

/* ------------------------------------------------------------- boot */

if (!(/** @type {any} */ (window).__sbotGift)) {
  /** @type {any} */ (window).__sbotGift = true;

  // toda re-renderização da seção (gaveta, ou o estado na página /cart) reavalia
  const wrapper = document.getElementById(`shopify-section-${SECTION_ID}`);
  if (wrapper) {
    const touchesGift = (/** @type {Node} */ node) =>
      node instanceof Element && (node.matches('[data-sbot-gift]') || Boolean(node.querySelector('[data-sbot-gift]')));
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' || [...mutation.addedNodes, ...mutation.removedNodes].some(touchesGift)) {
          schedule();
          return;
        }
      }
    }).observe(wrapper, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-sbot-stamp', 'data-unlocked', 'data-in-cart', 'data-has-gift-line'],
    });
  }

  // página /cart: o Horizon só re-renderiza o carrinho da página; o estado dos brindes (nossa seção) re-renderizamos aqui
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (hasDrawer()) return;
    /** @type {any} */ (event).promise
      ?.then((/** @type {any} */ result) => {
        if (result?.detail?.source === 'sbot-gift' || result?.detail?.didError) return;
        const html = result?.detail?.sections?.[SECTION_ID];
        if (html) morphSection(SECTION_ID, html, { mode: 'full' }).catch(() => {});
        else rerender();
      })
      .catch(() => {});
  });

  // checkout só depois do brinde no lugar (gaveta e página /cart)
  window.addEventListener('click', waitGiftsBeforeCheckout, { capture: true });
  window.addEventListener('submit', waitGiftsBeforeCheckout, { capture: true });

  // carregamento: carrinho que já chega cheio (link /cart/add, volta de outra página) — o initToggleUpsells do tema-ref
  schedule(400);
}
